import { createTheme } from '@mui/material/styles';
import type { CSSProperties } from 'react';
import { QualityLevel } from '@/api-gen/api.schemas';
import * as tokens from './tokens';

// Quality level color mapping for theme. Values come from the shared tokens so
// MUI and Tailwind stay in sync; this map keeps the QualityLevel enum keys.
const QualityLevelColor = {
  [QualityLevel.Unset]: tokens.qualityLevel.unset,
  [QualityLevel.Good]: tokens.qualityLevel.good,
  [QualityLevel.Acceptable]: tokens.qualityLevel.acceptable,
  [QualityLevel.Bad]: tokens.qualityLevel.bad
} as const;

type QualityLevelColorType = typeof QualityLevelColor;

// Conversation score tier colors. Distinct from QualityLevel (which is the
// flow-chart label palette) — these drive the conversation detail panel and
// list. Excellent ≥75, OnTrack ≥25, NeedsWork <25, Pending = no score yet.
type TierToken = { dot: string; fg: string; bg: string };
const Tier = tokens.tier;
type TierColorType = Record<'excellent' | 'onTrack' | 'needsWork' | 'pending', TierToken>;

// Score band thresholds — the policy that drives tier assignment.
export const TIER_BANDS = {
  excellent: { min: 75, max: 100 },
  onTrack: { min: 25, max: 74 },
  needsWork: { min: 0, max: 24 }
} as const;

// Reusable elevation shadows. Keeps both V2 panel shadows in one place.
export const ELEVATION = tokens.elevation;
import { inputBaseClasses } from '@mui/material/InputBase';

declare module '@mui/styles' {
  interface DefaultTheme {
    spacing: (value1: number, value2?: number, value3?: number, value4?: number) => string | number;
    transitions: {
      create: (
        props: string | string[],
        options?: {
          duration?:
            | number
            | {
                shortest: number;
                shorter: number;
                short: number;
                standard: number;
                complex: number;
                enteringScreen: number;
                leavingScreen: number;
              };
          easing?: string | { easeInOut: string; easeOut: string; easeIn: string; sharp: string };
          delay?: number;
        }
      ) => string;
      duration: {
        shortest: number;
        shorter: number;
        short: number;
        standard: number;
        complex: number;
        enteringScreen: number;
        leavingScreen: number;
      };
      easing: {
        easeInOut: string;
        easeOut: string;
        easeIn: string;
        sharp: string;
      };
    };
    shape: {
      borderRadius: number;
    };
    palette: {
      action: {
        hover: string;
      };
      divider: string;
    };
  }
}

declare module '@mui/material/styles/createPalette.js' {
  interface Palette {
    qualityLevel: QualityLevelColorType;
    tier: TierColorType;
  }

  interface PaletteOptions {
    qualityLevel: QualityLevelColorType;
    tier: TierColorType;
  }

  interface TypeBackground {
    dark?: {
      200: string;
      300: string;
      400: string;
      500: string;
      600: string;
    };
  }

  interface PaletteColor {
    950?: string;
  }

  interface SimplePaletteColorOptions {
    950?: string;
  }
}

declare module '@mui/material/styles/createMixins.js' {
  interface Mixins {
    denseToolbar: CSSProperties;
  }
}

declare module '@mui/material/styles' {
  interface TypographyVariants {
    body3: CSSProperties;
    tableCell: CSSProperties;
    tableHeader: CSSProperties;
  }

  interface TypographyVariantsOptions {
    body3?: CSSProperties;
    tableCell?: CSSProperties;
    tableHeader?: CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    body3: true;
    tableCell: true;
    tableHeader: true;
  }
}

const theme = createTheme({
  mixins: {
    denseToolbar: {
      minHeight: 72
    }
  },
  typography: {
    body3: {
      fontFamily: ['"Roboto"', '"Helvetica"', '"Arial"', 'sans-serif'].join(','),
      fontWeight: 400,
      fontSize: '0.8125rem',
      lineHeight: 1.43,
      letterSpacing: '0.01071em'
    },
    tableCell: {
      fontFamily: ['"Roboto"', '"Helvetica"', '"Arial"', 'sans-serif'].join(','),
      fontWeight: 400,
      fontSize: '0.9375rem',
      lineHeight: 1.35,
      letterSpacing: '0.01em'
    },
    tableHeader: {
      fontFamily: ['"Roboto"', '"Helvetica"', '"Arial"', 'sans-serif'].join(','),
      fontWeight: 500,
      fontSize: '0.6875rem',
      lineHeight: 1.4,
      letterSpacing: '0.08em',
      textTransform: 'uppercase'
    }
  },
  palette: {
    secondary: { ...tokens.secondary },
    primary: { ...tokens.primary },
    grey: { ...tokens.grey },
    background: {
      dark: { ...tokens.backgroundDark }
    },
    success: { ...tokens.success },
    error: { ...tokens.error },
    info: { ...tokens.info },
    qualityLevel: QualityLevelColor,
    tier: Tier
  },
  components: {
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          '&.MuiButton-containedPrimary:hover': {
            backgroundColor: 'rgba(15, 19, 36, 0.85)' // Primary 950 with 0.85 opacity. Remove or change this if using something else than 950 as primary color
          }
        }
      }
    },
    MuiFab: {
      styleOverrides: {
        root: {
          '&.MuiFab-primary:hover': {
            backgroundColor: 'rgba(15, 19, 36, 0.85)' // Primary 950 with 0.85 opacity. Remove or change this if using something else than 950 as primary color
          }
        }
      }
    },
    MuiCssBaseline: {
      styleOverrides: {
        '.text-truncate': {
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        },
        '.full-width': {
          width: '100% !important'
        }
      }
    },
    MuiMenu: {
      styleOverrides: {
        list: {
          // https://github.com/mui-org/material-ui/issues/25064#issuecomment-783948395
          backgroundColor: 'inherit'
        }
      }
    },
    MuiListSubheader: {
      styleOverrides: {
        root: {
          // https://github.com/mui-org/material-ui/issues/18200#issuecomment-578349237
          pointerEvents: 'none'
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          [`&.${inputBaseClasses.disabled}`]: {
            color: '#777'
          }
        }
      }
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          body3: 'p',
          tableCell: 'span',
          tableHeader: 'span'
        }
      }
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.08)'
            }
          }
        }
      }
    }
  }
});

export default theme;
